import { ClinicalRationaleMatch } from '@hb/shared'
import { createContext, ReactNode, useContext } from 'react'

// Centralizes the encounter-form autofill + clinical-rationale state so deeply-nested section inputs
// (the Notes section's "Regenerate" button, the clinical-rationale section's suggestions + "Fetch from
// notes" button) can read/trigger them without threading callbacks through renderSection.
export interface AutofillContextValue {
  autofilling: boolean
  // True once the provider has advanced past the notes step on an autofill-supported encounter type.
  canRegenerateAutofill: boolean
  // Re-run the autofill from the current notes value, overwriting previously-filled fields.
  regenerateAutofill: () => void
  // Suggestions bundled from the last autofill run, or fetched on demand. null = none fetched yet.
  clinicalRationaleSuggestions: ClinicalRationaleMatch[] | null
  rationaleLoading: boolean
  // Fetch clinical-rationale suggestions for the given chief-complaint text and store them.
  fetchRationaleSuggestions: (chiefComplaint: string) => void
}

const AutofillContext = createContext<AutofillContextValue>({
  autofilling: false,
  canRegenerateAutofill: false,
  regenerateAutofill: () => {},
  clinicalRationaleSuggestions: null,
  rationaleLoading: false,
  fetchRationaleSuggestions: () => {},
})

export const AutofillProvider = ({
  value,
  children,
}: {
  value: AutofillContextValue
  children: ReactNode
}) => <AutofillContext.Provider value={value}>{children}</AutofillContext.Provider>

export const useAutofill = () => useContext(AutofillContext)
